import { emptySnapshot, type PresenceApi } from './presenceAdapter';
import { APP_ACTIVITY } from './presenceResolver';
import type { ActivityState, PresenceSnapshot } from './types';

/**
 * Typed mock implementing the exact `PresenceApi` contract. Swapping to live
 * data is a one-line change in `presenceClient.ts`.
 *
 * Deltas are emitted on a single shared 4s timer for ALL subscribers and are
 * batched into one callback — the same coalescing the gateway does at 250ms.
 */
const SEED: ReadonlyArray<readonly [string, ActivityState, string | null, string | null]> = [
  ['u-serena', 'available', '🎧', 'Focus time'],
  ['u-babak', 'inAMeeting', null, null],
  ['u-audi', 'available', null, null],
  ['u-charlotte', 'inACall', '✈️', 'Working remotely'],
  ['u-priya', 'away', null, null],
  ['u-marco', 'doNotDisturb', '🚧', 'Shipping the release'],
  ['u-lena', 'presenting', null, null],
  ['u-tom', 'offline', null, null],
  ['u-nadia', 'outOfOffice', '🌴', 'Back on Monday'],
];

const ROTATION: readonly ActivityState[] = ['available', 'busy', 'away', 'inAMeeting'];

function seededSnapshot(index: number): PresenceSnapshot {
  const row = SEED[index];
  if (!row) return emptySnapshot(`u-${index}`);
  const [userId, state, emoji, text] = row;
  const base = emptySnapshot(userId);
  return {
    ...base,
    autoState: state,
    /* The mock stands in for the SERVER, so it fills the field the server fills:
       a seeded `inACall` / `presenting` row carries an activity, exactly as
       `cyphex.user_presence_live` would. Leaving it null here would make the
       mock the one place the resolver still sees the old, broken shape. */
    activityState: APP_ACTIVITY.has(state) ? state : null,
    effectiveState: state,
    connectionCount: state === 'offline' ? 0 : 1,
    status: text ? { emoji, text, expiresAt: null, pinInChat: false } : null,
  };
}

const snapshots = new Map<string, PresenceSnapshot>(
  SEED.map((row, index): [string, PresenceSnapshot] => [row[0], seededSnapshot(index)]),
);

const listeners = new Set<(rows: PresenceSnapshot[]) => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let tick = 0;

function ensureTimer(): void {
  if (timer !== null || listeners.size === 0) return;
  timer = setInterval(() => {
    tick += 1;
    const target = SEED[tick % SEED.length];
    if (!target) return;
    const current = snapshots.get(target[0]);
    if (!current || current.effectiveState === 'offline') return;
    const rotated = ROTATION[tick % ROTATION.length] ?? 'available';
    const next: PresenceSnapshot = {
      ...current,
      autoState: rotated,
      activityState: APP_ACTIVITY.has(rotated) ? rotated : null,
      effectiveState: rotated,
      stateSince: Date.now(),
    };
    snapshots.set(next.userId, next);
    for (const listener of listeners) listener([next]);
  }, 4000);
}

function stopTimer(): void {
  if (timer !== null && listeners.size === 0) {
    clearInterval(timer);
    timer = null;
  }
}

export const presenceMock: PresenceApi = {
  async fetchSelf() {
    return {
      userId: 'u-me',
      activityState: null,
      manualState: null,
      manualExpiresAt: null,
      status: null,
      ooo: null,
      automation: { autoAwayAfterMinutes: 5, dndDuringMeetings: true, dndDuringCalls: true },
    };
  },
  async fetchMany(userIds) {
    return userIds.map((id) => snapshots.get(id) ?? emptySnapshot(id));
  },
  async setManualState() {
    /* The optimistic client update is authoritative in the mock. */
  },
  async setCustomStatus() {},
  async heartbeat() {},
  subscribe(userIds, onDelta) {
    listeners.add(onDelta);
    ensureTimer();
    /* The contract, honoured: the current state first, the changes after. */
    onDelta(userIds.map((id) => snapshots.get(id) ?? emptySnapshot(id)));
    return () => {
      listeners.delete(onDelta);
      stopTimer();
    };
  },
};
